import { ArrowRight, Guitar, Library, Radio, Music2, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import gitarreTeamImg from "@/assets/gitarre-online-lernen.jpg";

const pathSteps = [
  {
    id: 1,
    icon: Guitar,
    title: "Gitarren-Kickstart",
    subtitle: "Dein Einstieg",
    color: "from-[#06B6D4] to-[#0891B2]",
    bgColor: "bg-[#ECFEFF]",
    ringColor: "ring-[#06B6D4]/20",
    accent: "#06B6D4",
  },
  {
    id: 2,
    icon: Library,
    title: "Selbstlernkurse",
    subtitle: "Viele Module – dein Tempo",
    color: "from-[#F59E0B] to-[#D97706]",
    bgColor: "bg-[#FEF3C7]",
    ringColor: "ring-[#F59E0B]/20",
    accent: "#F59E0B",
  },
  {
    id: 3,
    icon: Radio,
    title: "7 Live-Webinare",
    subtitle: "Live mit Anne Lüken",
    color: "from-[#EC4899] to-[#DB2777]",
    bgColor: "bg-[#FCE7F3]",
    ringColor: "ring-[#EC4899]/20",
    accent: "#EC4899",
  },
  {
    id: 4,
    icon: Music2,
    title: "Liederwerkstatt Gitarrissimo",
    subtitle: "Frische Lieder jeden Monat",
    color: "from-[#8B5CF6] to-[#7C3AED]",
    bgColor: "bg-[#F5F3FF]",
    ringColor: "ring-[#8B5CF6]/20",
    accent: "#8B5CF6",
  },
];

const finalStep = {
  icon: Trophy,
  title: "Dein Zertifikat",
  subtitle: "Offizieller Abschluss",
  color: "from-[#10B981] to-[#059669]",
  bgColor: "bg-[#ECFDF5]",
};

export const GitarreInteractivePathSection = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={gitarreTeamImg}
          alt="Fachkraft Gitarre"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-primary/10" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <Badge className="mb-4 bg-accent/10 text-accent hover:bg-accent/20">
            Dein Weg
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">
            Dein Weg zum{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              großen Kita-Gitarrenkurs
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Vier Bausteine – ein Abschluss
          </p>
        </div>

        {/* Steps */}
        <div className="max-w-7xl mx-auto">
          {/* Desktop: Horizontal Steps */}
          <div className="hidden lg:flex items-stretch justify-between gap-3">
            {pathSteps.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div
                  className={`group relative flex flex-col items-center gap-4 p-6 rounded-2xl border-2 border-primary/10 shadow-lg ${step.bgColor} w-full animate-fade-in transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03] hover:shadow-2xl hover:border-primary/30`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center text-sm font-bold text-primary border-2 border-primary/20">
                    {step.id}
                  </div>
                  <div
                    className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} shadow-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}
                  >
                    <step.icon className="w-10 h-10 text-white" strokeWidth={1.75} />
                  </div>
                  <div className="text-center">
                    <h3 className="font-bold text-base mb-1 text-foreground leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {step.subtitle}
                    </p>
                  </div>
                </div>

                {index < pathSteps.length - 1 && (
                  <ArrowRight className="w-6 h-6 text-primary/60 flex-shrink-0 mx-1 animate-arrow-pulse" />
                )}
              </div>
            ))}
          </div>

          {/* Mobile: Vertical Steps */}
          <div className="lg:hidden space-y-4">
            {pathSteps.map((step, index) => (
              <div key={step.id}>
                <div
                  className={`group flex items-center gap-4 p-5 rounded-xl border-2 border-primary/10 shadow-md ${step.bgColor} transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}
                >
                  <div
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${step.color} shadow flex-shrink-0 flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}
                  >
                    <step.icon className="w-7 h-7 text-white" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-base text-foreground">
                      <span className="text-primary mr-2">{step.id}.</span>
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {step.subtitle}
                    </p>
                  </div>
                </div>
                {index < pathSteps.length - 1 && (
                  <div className="flex justify-center py-2">
                    <ArrowRight className="w-6 h-6 text-primary/60 rotate-90 animate-arrow-pulse" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Final Certificate Step */}
          <div className="mt-10 flex justify-center animate-fade-in" style={{ animationDelay: "0.5s" }}>
            <div className="flex flex-col items-center gap-2">
              <ArrowRight className="w-8 h-8 text-primary/60 rotate-90 animate-arrow-pulse" />
              <div
                className={`group relative flex items-center gap-5 p-6 lg:p-8 rounded-2xl border-2 border-[#10B981]/30 shadow-xl ${finalStep.bgColor} transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl`}
              >
                <div
                  className={`w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-gradient-to-br ${finalStep.color} shadow-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}
                >
                  <finalStep.icon className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-xl lg:text-2xl text-foreground">
                    {finalStep.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {finalStep.subtitle}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div
          className="text-center mt-12 animate-fade-in"
          style={{ animationDelay: "0.2s" }}
        >
          <Button
            size="lg"
            className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
            asChild
          >
            <Link to="/ausbildung/fachkraft-gitarre/kaufen">
              Jetzt zum Kurs anmelden
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
